import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';

const HowWeWorkSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const trustPoints = [
    { icon: 'ri-shield-check-line', text: 'Verified suppliers' },
    { icon: 'ri-eye-line', text: 'Transparent process' },
    { icon: 'ri-map-pin-line', text: 'Canada-based support' },
  ];

  return (
    <section ref={ref} className="py-24 sm:py-32 lg:py-36 bg-neutral-light">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            className="relative order-2 lg:order-1 hidden lg:block"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="relative h-[400px] sm:h-[500px] flex flex-col justify-center items-center">
              {/* Factory Icon */}
              <motion.div
                className="w-20 h-20 sm:w-24 sm:h-24 bg-navy-primary rounded-2xl flex items-center justify-center mb-8 sm:mb-12"
                initial={{ opacity: 0, x: -30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <i className="ri-building-line text-white text-3xl sm:text-4xl"></i>
              </motion.div>

              {/* Connecting Line */}
              <motion.div
                className="w-1 bg-gold-accent mb-8 sm:mb-12"
                initial={{ height: 0 }}
                animate={isInView ? { height: window.innerWidth < 640 ? '60px' : '80px' } : {}}
                transition={{ duration: 0.8, delay: 0.5 }}
              />

              {/* QC Checkmark */}
              <motion.div
                className="w-20 h-20 sm:w-24 sm:h-24 bg-gold-accent rounded-2xl flex items-center justify-center mb-8 sm:mb-12 relative"
                initial={{ opacity: 0, scale: 0 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gold-accent/30 rounded-2xl"
                  animate={isInView ? { scale: [1, 1.2, 1] } : {}}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <i className="ri-checkbox-circle-line text-white text-3xl sm:text-4xl relative z-10"></i>
              </motion.div>

              {/* Connecting Line */}
              <motion.div
                className="w-1 bg-gold-accent mb-8 sm:mb-12"
                initial={{ height: 0 }}
                animate={isInView ? { height: window.innerWidth < 640 ? '60px' : '80px' } : {}}
                transition={{ duration: 0.8, delay: 1 }}
              />

              {/* Shipping Box */}
              <motion.div
                className="w-20 h-20 sm:w-24 sm:h-24 bg-navy-primary rounded-2xl flex items-center justify-center"
                initial={{ opacity: 0, x: -30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <i className="ri-ship-line text-white text-3xl sm:text-4xl"></i>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            className="order-1 lg:order-2"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-primary mb-5 sm:mb-7 leading-tight">
              Built for Reliable
              <br />
              <span className="text-gold-accent">Global Trade</span>
            </h2>

            <p className="text-base sm:text-lg text-navy-primary/70 leading-relaxed mb-7 sm:mb-9">
              We manage supplier discovery, product development, quality inspection, and export logistics so you can scale with confidence.
            </p>

            <div className="space-y-3 sm:space-y-4 mb-8 sm:mb-11">
              {trustPoints.map((point, index) => (
                <motion.div
                  key={point.text}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                >
                  <div className="w-6 h-6 bg-gold-accent rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${point.icon} text-white text-sm`}></i>
                  </div>
                  <span className="text-sm sm:text-base font-medium text-navy-primary">
                    {point.text}
                  </span>
                </motion.div>
              ))}
            </div>

            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-7 sm:px-9 py-3 sm:py-4 bg-navy-primary text-white rounded-full font-semibold text-sm sm:text-base hover:bg-gold-accent hover:text-navy-primary transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Explore Our Services
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HowWeWorkSection;